import type { SiteLang } from "@/hooks/useLanguage";

type EcosystemI18n = {
  seo: { title: string; description: string };
  breadcrumb: { about: string; ecosystem: string };
  hero: {
    kicker: string;
    titlePre: string;
    titleAccent: string;
    subtitle: string;
    ctaCompanies: string;
    ctaPartners: string;
  };
  companies: {
    kicker: string;
    title: string;
    subtitle: string;
  };
  partners: {
    kicker: string;
    title: string;
    subtitle: string;
  };
  visitWebsite: string;
};

const en: EcosystemI18n = {
  seo: {
    title: "Ecosystem | QbD Group Partners & Companies",
    description: "Discover QbD Group's comprehensive ecosystem of companies and strategic partners delivering expertise across the life sciences industry.",
  },
  breadcrumb: { about: "About us", ecosystem: "Ecosystem" },
  hero: {
    kicker: "Our ecosystem",
    titlePre: "Alone you can do something,",
    titleAccent: "together you can do anything.",
    subtitle: "Our <strong class=\"text-primary-foreground\">comprehensive ecosystem</strong> delivers expertise across development, clinical, regulatory, compliance, production, and distribution for pharma, biotech, and medical device companies.",
    ctaCompanies: "Our companies",
    ctaPartners: "Strategic partners",
  },
  companies: {
    kicker: "Integrated specialist delivery",
    title: "Companies in the QbD Group",
    subtitle: "Each company within the group brings unique expertise and capabilities, strengthening our ability to deliver high-quality solutions across various industries.",
  },
  partners: {
    kicker: "Complementary global expertise",
    title: "QbD Group Partner Companies",
    subtitle: "Through strategic partnerships, we leverage complementary expertise to provide enhanced services and drive mutual success across the global marketplace.",
  },
  visitWebsite: "Visit website",
};

const es: EcosystemI18n = {
  seo: {
    title: "Ecosistema | Socios y empresas de QbD Group",
    description: "Descubre el ecosistema integral de empresas y socios estratégicos de QbD Group que aportan experiencia en toda la industria Life Sciences.",
  },
  breadcrumb: { about: "Sobre nosotros", ecosystem: "Ecosistema" },
  hero: {
    kicker: "Nuestro ecosistema",
    titlePre: "Solo puedes hacer algo,",
    titleAccent: "juntos puedes hacerlo todo.",
    subtitle: "Nuestro <strong class=\"text-primary-foreground\">ecosistema integral</strong> ofrece experiencia en desarrollo, clínica, regulatoria, cumplimiento, producción y distribución para empresas farmacéuticas, biotecnológicas y de dispositivos médicos.",
    ctaCompanies: "Nuestras empresas",
    ctaPartners: "Socios estratégicos",
  },
  companies: {
    kicker: "Especialidades integradas",
    title: "Empresas del grupo QbD",
    subtitle: "Cada empresa del grupo aporta experiencia y capacidades únicas, fortaleciendo nuestra capacidad de ofrecer soluciones de alta calidad en diversas industrias.",
  },
  partners: {
    kicker: "Especialización complementaria",
    title: "Empresas socias de QbD Group",
    subtitle: "A través de alianzas estratégicas, aprovechamos la experiencia complementaria para ofrecer servicios mejorados e impulsar el éxito mutuo en el mercado global.",
  },
  visitWebsite: "Visitar sitio web",
};

const zh: EcosystemI18n = {
  seo: {
    title: "生态系统 | QbD Group 合作伙伴与公司",
    description: "了解 QbD Group 的综合生态系统——旗下公司与战略合作伙伴在生命科学行业提供全方位专业能力。",
  },
  breadcrumb: { about: "关于我们", ecosystem: "生态系统" },
  hero: {
    kicker: "我们的生态系统",
    titlePre: "独自一人能成就一些事，",
    titleAccent: "携手则无所不能。",
    subtitle: "我们的<strong class=\"text-primary-foreground\">综合生态系统</strong>为制药、生物技术和医疗器械企业提供涵盖开发、临床、法规、合规、生产和分销的专业能力。",
    ctaCompanies: "我们的公司",
    ctaPartners: "战略合作伙伴",
  },
  companies: {
    kicker: "专长互补协同交付",
    title: "QbD Group 旗下公司",
    subtitle: "集团内的每家公司都带来独特的专业能力，增强我们在多个行业提供高质量解决方案的能力。",
  },
  partners: {
    kicker: "全球专长互补协作",
    title: "QbD Group 合作伙伴公司",
    subtitle: "通过战略合作伙伴关系，我们利用互补的专业能力提供更优质的服务，并在全球市场中实现共同成功。",
  },
  visitWebsite: "访问网站",
};

const translations: Record<SiteLang, EcosystemI18n> = { en, es, "zh-cn": zh };
export const getEcosystemT = (lang: SiteLang) => translations[lang] ?? en;
